import {
  DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT,
  normalizeDispatchColorIntensityPercent,
} from "./userUiPreferences.js";

export const DISPATCH_COLOR_INTENSITY_CSS_VARIABLES = Object.freeze({
  chipFillDelta: "--dispatch-chip-fill-delta",
  coloredBorderDelta: "--dispatch-colored-border-delta",
  percent: "--dispatch-color-intensity-percent",
  segmentBorderDelta: "--dispatch-segment-border-delta",
  segmentFillDelta: "--dispatch-segment-fill-delta",
  softFillDelta: "--dispatch-soft-fill-delta",
});

export const DISPATCH_COLOR_INTENSITY_STOPS = Object.freeze([
  Object.freeze({
    chipFillDelta: 0.13,
    coloredBorderDelta: 0.13,
    percent: -20,
    segmentBorderDelta: 0.19,
    segmentFillDelta: 0.13,
    softFillDelta: 0.13,
  }),
  Object.freeze({
    chipFillDelta: 0.19,
    coloredBorderDelta: 0.19,
    percent: -10,
    segmentBorderDelta: 0.29,
    segmentFillDelta: 0.19,
    softFillDelta: 0.19,
  }),
  Object.freeze({
    chipFillDelta: 0.215,
    coloredBorderDelta: 0.215,
    percent: 0,
    segmentBorderDelta: 0.39,
    segmentFillDelta: 0.215,
    softFillDelta: 0.215,
  }),
  Object.freeze({
    chipFillDelta: 0.24,
    coloredBorderDelta: 0.24,
    percent: 10,
    segmentBorderDelta: 0.49,
    segmentFillDelta: 0.24,
    softFillDelta: 0.24,
  }),
  Object.freeze({
    chipFillDelta: 0.25,
    coloredBorderDelta: 0.25,
    percent: 30,
    segmentBorderDelta: 0.69,
    segmentFillDelta: 0.25,
    softFillDelta: 0.25,
  }),
  Object.freeze({
    chipFillDelta: 0.25,
    coloredBorderDelta: 0.25,
    percent: 60,
    segmentBorderDelta: 0.99,
    segmentFillDelta: 0.25,
    softFillDelta: 0.25,
  }),
]);

const DELTA_KEYS = Object.freeze([
  "segmentFillDelta",
  "segmentBorderDelta",
  "softFillDelta",
  "chipFillDelta",
  "coloredBorderDelta",
]);

function roundDelta(value) {
  const rounded = Math.round((value + Number.EPSILON) * 100_000) / 100_000;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function findIntensityBounds(percent) {
  for (let index = 1; index < DISPATCH_COLOR_INTENSITY_STOPS.length; index += 1) {
    const upper = DISPATCH_COLOR_INTENSITY_STOPS[index];
    if (percent <= upper.percent) {
      return {
        lower: DISPATCH_COLOR_INTENSITY_STOPS[index - 1],
        upper,
      };
    }
  }

  const last = DISPATCH_COLOR_INTENSITY_STOPS.at(-1);
  return { lower: last, upper: last };
}

export function resolveDispatchColorIntensityProfile(
  value = DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT,
) {
  const percent = normalizeDispatchColorIntensityPercent(value);
  const { lower, upper } = findIntensityBounds(percent);
  const progress = lower.percent === upper.percent
    ? 0
    : (percent - lower.percent) / (upper.percent - lower.percent);

  const profile = { percent };
  for (const key of DELTA_KEYS) {
    profile[key] = roundDelta(
      lower[key] + ((upper[key] - lower[key]) * progress),
    );
  }

  profile.neutralDeltas = Object.freeze({
    chipFillDelta: roundDelta(profile.chipFillDelta * 0.5),
    coloredBorderDelta: roundDelta(profile.coloredBorderDelta * 0.5),
    segmentBorderDelta: roundDelta(profile.segmentBorderDelta * 0.5),
    segmentFillDelta: roundDelta(profile.segmentFillDelta * 0.5),
    softFillDelta: roundDelta(profile.softFillDelta * 0.5),
  });

  return Object.freeze(profile);
}

export function buildDispatchColorIntensityCssVariables(value) {
  const profile = resolveDispatchColorIntensityProfile(value);
  return Object.freeze(Object.fromEntries(
    Object.entries(DISPATCH_COLOR_INTENSITY_CSS_VARIABLES).map(([profileKey, cssVariable]) => [
      cssVariable,
      String(profile[profileKey]),
    ]),
  ));
}

export function clearDispatchColorIntensityCssVariables(target) {
  if (!target?.style || typeof target.style.removeProperty !== "function") {
    return;
  }

  for (const cssVariable of Object.values(DISPATCH_COLOR_INTENSITY_CSS_VARIABLES)) {
    target.style.removeProperty(cssVariable);
  }
}

export function applyDispatchColorIntensityCssVariables(target, value) {
  if (
    !target?.style
    || typeof target.style.getPropertyValue !== "function"
    || typeof target.style.setProperty !== "function"
  ) {
    return () => {};
  }

  const cssVariables = buildDispatchColorIntensityCssVariables(value);
  const previousValues = Object.fromEntries(
    Object.keys(cssVariables).map((cssVariable) => [cssVariable, {
      priority: typeof target.style.getPropertyPriority === "function"
        ? target.style.getPropertyPriority(cssVariable)
        : "",
      value: target.style.getPropertyValue(cssVariable),
    }]),
  );

  for (const [cssVariable, cssValue] of Object.entries(cssVariables)) {
    target.style.setProperty(cssVariable, cssValue);
  }

  return () => {
    for (const [cssVariable, previous] of Object.entries(previousValues)) {
      if (previous.value) {
        target.style.setProperty(cssVariable, previous.value, previous.priority);
      } else {
        target.style.removeProperty(cssVariable);
      }
    }
  };
}
